import { JSX, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { teamsState } from '../../recoil/atoms/teamsState';
import { playerTeamDataSelector } from '../../recoil/selectors/playerTeamDataSelector';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import LockIcon from '@mui/icons-material/Lock';
import RestoreIcon from '@mui/icons-material/Restore';
import PublicIcon from '@mui/icons-material/Public';
import BuildIcon from '@mui/icons-material/Build';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';


import { translate } from '../../../common/language/translations';
import { IconButton } from '../controls/buttons/IconButton';
import { getAccessTableOptionIcon, getCurrentTeamIcon, getLocalityIcon, getRoleIcon } from '../../utils/iconUtils';
import { PlayerImage } from '../display/images/PlayerImage';
import { CountryFlag } from '../display/images/CountryFlag';
import { ClubLogo } from '../display/images/ClubLogo';
import { ProgressCircle } from '../display/visualization/ProgressCircle';
import { getColorBlindRatingColor, getIndexColor, getRatingColor } from '../../utils/colorUtils';
import { dateStringToTextFormat } from '../../utils/dateUtils';
import { countryCodeToCountryInfo } from '../../static/countries';
import { getDisplayEconomicValue } from '../../utils/economyUtils';
import { CompetitionFlag } from '../display/images/CompetitionFlag';
import { playersState } from '../../recoil/atoms/playersState';
import { addPlayerToAcademy } from '../../services/firestore/players';
import { FirebaseUser } from '../../../firebase';
import { CareerTableCellProps, CareerTableColumnCellProps } from './careerTable/careerColumns';
import { getDisplayPositions, getPrimaryAndSecondaryPositionsForMultipleIterations } from '../../utils/positionUtils';
import { IndexBar } from '../display/visualization/IndexBar';
import { playerMetricMap } from '../../static/playerMetrics';
import { PlayerClubIteration, PlayerClubIterationPositionGroupData, PlayerId, PlayerOverview, PositionGroupClubIteration } from '../../types/player';
import { getDisplayPlayerName } from '../../utils/nameUtils';
import { StringToAnyMap } from '../../types/common';
import { RoleConfig } from '../../types/tenant';
import { getAge } from '../../utils/ageUtils';
import { getMonthsLeftOfContract } from '../../utils/contractUtils';
import { getRoundedIndexValue, getNormalizedMetricName, isSkillcornerMetric, isPhysicalRoleConfig, roleConfigIsBeingComputed } from '../../utils/metricUtils';
import { getTenantLightColor } from '../../static/tenantConfigs';
import { Toggle } from '../controls/input/Toggle';
import { Dialog } from '@mui/material';
import { modalPaperProps } from '../../static/constants/modals';
import { AccessTableOptionModal } from '../modals/accounModals/AccessTableOptionModal';
import { AccessConfig } from '../../types/user';


interface PlayerPaginationCellProps {
  totalHits: number;
  currentChapter: number; // or page if chapterless
  hitsPerChapter: number; // or hitsPerPage if chapterless
  handleChangeCurrentChapter: (isIncrement: boolean) => void;
  paginationSectionPaddingRight?: number;
  language?: string;
}


export const PlayerPaginationCell: React.FC<PlayerPaginationCellProps> = ({
  totalHits,
  currentChapter,
  hitsPerChapter,
  handleChangeCurrentChapter,
  paginationSectionPaddingRight,
  language,
}) => {

  return (
    <div className='player-pagination-cell'>
      {totalHits + (totalHits >= 10000 ? '+ ' : ' ') + translate(totalHits === 1 ? 'player' : 'players', language, true)}

      {totalHits > hitsPerChapter && (
        <div className='table-cell' style={{ justifyContent: 'start' }}>

          <div className='player-pagination-cell-current-chapter-range'>
            {'(' + (hitsPerChapter * currentChapter + 1) + '-' + Math.min(hitsPerChapter * (currentChapter + 1), totalHits) + ')'}
          </div>

          <div className='player-pagination-cell-pagination-section' style={{ paddingRight: paginationSectionPaddingRight }}>
            <IconButton
              icon={(
                <KeyboardArrowLeftIcon
                  style={{ fontSize: 17, color: currentChapter > 0 ? '#ffffffcc' : '#ffffff77' }}
                />
              )}
              onClick={() => currentChapter > 0 ? handleChangeCurrentChapter(false) : null}
              size={20}
              isMobile={true}
              style={{ cursor: currentChapter > 0 ? 'pointer' : 'default' }}
            />

            <div className='player-pagination-cell-current-chapter'>
              {currentChapter + 1}
            </div>

            <IconButton
              icon={(
                <KeyboardArrowRightIcon
                  style={{ fontSize: 17, color: currentChapter < Math.floor(totalHits / hitsPerChapter) ? '#ffffffcc' : '#ffffff77' }}
                />
              )}
              onClick={() => currentChapter < Math.floor(totalHits / hitsPerChapter) ? handleChangeCurrentChapter(true) : null}
              size={20}
              isMobile={true}
              style={{ cursor: currentChapter < Math.floor(totalHits / hitsPerChapter) ? 'pointer' : 'default' }}
            />
          </div>

        </div>
      )}
    </div>
  );
};


interface PlayerInfoCellProps {
  playerOverview?: PlayerOverview;
  positionGroupClubIteration?: PositionGroupClubIteration;
  infoCellColumnWidth?: number | string;
  language?: string;
  maxPlayerNameLength?: number;
  showCountryFlag?: boolean;
  showClubInfo?: boolean;
  showTeamIcon?: boolean;
  countryFlagWidth?: number;
}

export const PlayerInfoCell: React.FC<PlayerInfoCellProps> = ({
  playerOverview,
  positionGroupClubIteration,
  infoCellColumnWidth,
  language,
  maxPlayerNameLength = 22,
  showCountryFlag = true,
  showClubInfo = true,
  showTeamIcon = true,
  countryFlagWidth = 16,
}) => {

  const playerTeamData = useRecoilValue(playerTeamDataSelector) ?? {};
  const teams = useRecoilValue(teamsState) ?? {};
  const { openPlayerViewModal } = useOpenGlobalModal();

  // either one should be provided
  const playerObject = playerOverview ?? positionGroupClubIteration;
  if (!playerObject) return null;

  let teamIcon: JSX.Element | undefined = undefined;
  if (showTeamIcon && playerOverview) {

    const currentTeamIds = playerTeamData[playerOverview.id] ? Object.keys(playerTeamData[playerOverview.id]) : [];
    const teamEntries = currentTeamIds.map(teamId => ({ teamId, isAcademyTeam: teams[teamId]?.isAcademyTeam ?? false }));
    let currentTeamIcon = getCurrentTeamIcon(teamEntries, 18, 'dark');
    if (!currentTeamIcon) {
      currentTeamIcon = <AddIcon style={{ fontSize: 13 }} />;
    }

    teamIcon = (
      <IconButton
        onClick={(event) => {
          if (event) event.stopPropagation();
          openPlayerViewModal(
            playerOverview.id,
            playerOverview.name,
            undefined,
            true
          );
        }}
        icon={currentTeamIcon}
        size={24}
        title={translate(currentTeamIcon ? 'editTeamStatus' : 'addToTeam', language)}
        backgroundColorBehindIcon='gray'
      />
    );
  }

  let clubIterationInfo: JSX.Element | undefined = undefined;
  if (positionGroupClubIteration) {
    const primaryPositions = positionGroupClubIteration.overall_primary_positions ?? [];
    const secondaryPositions = positionGroupClubIteration.overall_secondary_positions ?? [];

    const primaryPositionString = getDisplayPositions(primaryPositions.slice(0, 3), language);
    let secondaryPositionString: string | undefined = undefined;

    if (primaryPositions.length === 1 && secondaryPositions.length > 0) {
      secondaryPositionString = getDisplayPositions(secondaryPositions.slice(0, 2), language);
    }

    clubIterationInfo = (
      <div className='player-info-cell-position-column'>
        <div className='player-info-cell-position-row' style={{ fontSize: 13 }}>
          {positionGroupClubIteration.season}
        </div>
        <div className='player-info-cell-position-row' style={{ fontSize: 12 }}>
          {primaryPositionString ?? '-'}

          {secondaryPositionString && (
            <div style={{ fontSize: 11, color: '#000000bb' }}>
              {'(' + secondaryPositionString + ')'}
            </div>
          )}
        </div>
      </div>
    );
  }


  return (
    <div className='player-info-cell'>

      <PlayerImage
        src={playerObject.image_url}
        size={40}
        containerStyle={{ marginLeft: 7, marginRight: 10 }}
        isSearchTableComponent={true}
      />


      <div className='player-info-cell-column' style={{ width: infoCellColumnWidth ?? '70%' }}>
        <div className='player-info-cell-row' style={{ fontSize: 13 }}>
          {getDisplayPlayerName(playerOverview?.name ?? positionGroupClubIteration?.player_name ?? '', maxPlayerNameLength)}

          {showCountryFlag && (
            <CountryFlag
              countryCode={playerObject.country_code}
              width={countryFlagWidth}
              isWhiteBackground={true}
              containerStyle={{ marginLeft: 9 }}
            />
          )}
        </div>

        {showClubInfo && (
          <ClubLogo
            club={playerObject.club}
            size={14}
            showClubName={true}
            competitionIdToShow={positionGroupClubIteration?.competition_id ?? playerObject.club?.current_competition_id}
            containerStyle={{ justifyContent: 'left', color: '#444444', fontSize: 12 }}
          />
        )}
      </div>

      {teamIcon}

      {clubIterationInfo}

    </div>
  );
};


interface PlayerSimpleTableInfoCellProps {
  row: StringToAnyMap;
  disablePlayersWithoutEventData?: boolean;
  isGoalkeeper?: boolean;
}

export const PlayerSimpleTableInfoCell: React.FC<PlayerSimpleTableInfoCellProps> = ({
  row,
  disablePlayersWithoutEventData,
  isGoalkeeper
}) => {

  const disablePlayer = (disablePlayersWithoutEventData && !row.event_data_available)
    || (isGoalkeeper !== undefined && (isGoalkeeper !== row.position_info?.primary_positions?.includes('GK')));

  return (
    <div className='table-cell'>

      <PlayerImage
        src={row.image_url}
        size={34}
        containerStyle={{ marginLeft: 1, marginRight: 4, padding: '2px 0px' }}
        isSearchTableComponent={true}
      />

      <div className='player-simple-table-info-cell-column'>

        <div className='player-simple-table-info-cell-row'>
          <div className='player-simple-table-info-cell-name' style={{ color: disablePlayer ? '#4e4e4e' : 'none' }}>
            {getDisplayPlayerName(row.name, 16)}
          </div>

          <CountryFlag
            countryCode={row.country_code}
            width={13}
            containerStyle={{ marginLeft: 3 }}
            isWhiteBackground={true}
          />
        </div>

        {row.club && (
          <div className='player-simple-table-info-cell-row'>
            <ClubLogo
              club={row.club}
              size={12}
              showClubName={true}
              containerStyle={{ color: disablePlayer ? '#4e4e4e' : '#444444', fontSize: 11, gap: 3 }}
            />
          </div>
        )}
      </div>
    </div>
  );
};


interface PlayerSimpleTableAddToAcademyCellProps {
  row: StringToAnyMap;
  onAddPlayerToAcademyClick: (() => void) | undefined;
  tenant: string;
  currentUser: FirebaseUser;
  language?: string;
}

export const PlayerSimpleTableAddToAcademyCell: React.FC<PlayerSimpleTableAddToAcademyCellProps> = ({
  row,
  onAddPlayerToAcademyClick,
  tenant,
  currentUser,
  language,
}) => {

  const players = useRecoilValue(playersState) ?? {};

  if (players[row.id]?.isAcademyPlayer) {
    return <CheckIcon style={{ fontSize: 16 }} />;
  }

  return (
    <div className='table-cell'>
      <div
        className='player-simple-table-add-to-academy-button'
        onClick={(e) => {
          e.stopPropagation();
          addPlayerToAcademy(row.id, row.name, tenant, currentUser);
          if (onAddPlayerToAcademyClick) onAddPlayerToAcademyClick();
        }}
      >
        {translate('add', language)}
        <AddIcon style={{ fontSize: 16 }} />
      </div>
    </div>
  );
};


interface AcademyTableRemovePlayerCellProps {
  row: StringToAnyMap,
  removePlayerFromAcademy: (playerId: PlayerId) => void;
  language?: string;
}

export const AcademyTableRemovePlayerCell: React.FC<AcademyTableRemovePlayerCellProps> = ({
  row,
  removePlayerFromAcademy,
  language,
}) => {

  if (row.name === 'tableSummary') return (
    <div className='table-cell'>
      -
    </div>
  );

  return (
    <div className='table-cell'>
      <div
        className='academy-table-remove-player-button'
        onClick={(e) => {
          e.stopPropagation();
          removePlayerFromAcademy(row.id);
        }}
      >
        {translate('removePlayer', language)}
        <PersonRemoveIcon style={{ fontSize: 16 }} />
      </div>
    </div>
  );
};


interface PositionCellProps {
  row: PlayerOverview | StringToAnyMap;
  language?: string;
  maxPositions?: number; // the maximum number of positions to display for each type
  isDarkBackground?: boolean;
  horizontal?: boolean;
}

export const PositionCell: React.FC<PositionCellProps> = ({
  row,
  language,
  maxPositions,
  isDarkBackground,
  horizontal,
}) => {

  let primaryPositions = [];
  let secondaryPositions = [];

  if ('shouldComputeNewPrimaryAndSecondaryPositions' in row) {
    const newPositions = getPrimaryAndSecondaryPositionsForMultipleIterations(row.position_info?.positions ?? []);
    primaryPositions = newPositions.primaryPositions;
    secondaryPositions = newPositions.secondaryPositions;
  }

  else if (row.event_data_exist) {
    primaryPositions = row.position_info?.primary_positions ?? [];
    secondaryPositions = row.position_info?.secondary_positions ?? [];

    if (maxPositions) {
      const allPositions = primaryPositions.concat(secondaryPositions);
      primaryPositions = allPositions.slice(0, maxPositions);
      secondaryPositions = allPositions.slice(maxPositions, maxPositions * 2);
    }
  }

  else {
    // if FAPI positions, we use the first position as primary and the second position as secondary (whether it's primary or secondary)
    const allPositions = (row.position_info?.primary_positions ?? []).concat(row.position_info?.secondary_positions ?? []);
    primaryPositions = allPositions.slice(0, 1);
    secondaryPositions = allPositions.slice(1);
  }

  const primaryPositionString = getDisplayPositions(primaryPositions, language);
  let secondaryPositionString = getDisplayPositions(secondaryPositions, language);
  if (horizontal && secondaryPositionString) {
    secondaryPositionString = '(' + secondaryPositionString + ')';
  }

  return (
    <div
      className={horizontal ? 'position-cell-horizontal' : 'position-cell'}
      style={{
        fontSize: primaryPositions.length > 2 ? 12 : undefined
      }}
    >
      {primaryPositionString ?? '-'}

      {secondaryPositionString && (
        <div style={{ fontSize: secondaryPositions.length > 2 ? 10 : 11, color: isDarkBackground ? '#ffffffdd' : '#000000dd' }}>
          {secondaryPositionString}
        </div>
      )}
    </div>
  );
};


interface IndexCellProps {
  playerOverview: PlayerOverview;
  property: string;
  positionGroup: string;
  showConfidence?: boolean;
}

export const IndexCell: React.FC<IndexCellProps> = ({
  playerOverview,
  property,
  positionGroup,
  showConfidence,
}) => {

  let value: number | string | undefined = undefined;
  let confidence = 0;
  let possibleConfidence = 0;

  const positionGroupStats = playerOverview.position_group_stats?.[positionGroup];
  if (positionGroupStats) {
    if (property === 'skill_index') {
      value = getRoundedIndexValue(positionGroupStats.skill_index?.index);
      confidence = positionGroupStats.skill_index?.confidence ?? 0;
      possibleConfidence = positionGroupStats.skill_index?.possible_confidence ?? 0;
    }

    else if (property === 'physical_index') {
      value = getRoundedIndexValue(positionGroupStats.physical_index?.index);
      confidence = positionGroupStats.physical_index?.confidence ?? 0;
      possibleConfidence = positionGroupStats.physical_index?.possible_confidence ?? 0;
    }

    else {
      value = getRoundedIndexValue(positionGroupStats.custom_indexes[property]?.index);
      confidence = positionGroupStats.custom_indexes[property]?.confidence ?? 0;
      possibleConfidence = positionGroupStats.custom_indexes[property]?.possible_confidence ?? 0;
    }
  }

  if (confidence === 0) {
    if (possibleConfidence > 0) {
      return (
        <div className='table-cell'>
          <LockIcon style={{ fontSize: 14, color: '#00000066' }} />
        </div>
      );
    }
    return (
      <div className='table-cell'>
        <BlockIcon style={{ fontSize: 14, color: '#00000066' }} />
      </div>
    );
  }

  return (
    <div className='table-cell flex-row align-center justify-center' style={{ gap: '15%' }}>
      <div className='flex-column align-center justify-center'>
        <div style={{ fontWeight: 500 }}>
          {value}
        </div>

        {value !== undefined && (
          <div style={{ marginTop: 5 }}>
            <IndexBar
              index={value}
              confidence={confidence}
              maxWidth={showConfidence ? 46 : 65}
              color={47}
              secondDimension={'height'}
              minHeight={1.5}
              trailColor='#00000022'
            />
          </div>
        )}
      </div>

      {showConfidence && confidence !== undefined && confidence !== null && (
        <ProgressCircle
          size={21}
          progress={confidence * 100}
          value={Math.round(confidence * 100)}
          trailColor='#00000022'
          color={getIndexColor(confidence * 10, 47)}
          fontSize={10}
        />
      )}
    </div>
  );
};


interface AlertTableExcludeCellProps {
  row: PlayerOverview;
  language?: string;
  excludePlayer?: (playerId: number, allAlerts: boolean, monthsDiscarded: number) => void;
}

export const AlertTableExcludeCell: React.FC<AlertTableExcludeCellProps> = ({
  row,
  language,
  excludePlayer,
}) => {

  const excludePlayerInternal = (event: React.MouseEvent, allAlerts: boolean, monthsDiscarded: number) => {
    event.stopPropagation();
    if (excludePlayer) {
      excludePlayer(row.id, allAlerts, monthsDiscarded);
    }
  };

  return (
    <div className='alert-table-action-cell'>

      <div className='alert-table-action-column alert-table-exclude-column' onClick={(event) => excludePlayerInternal(event, false, 3)}>
        <div className='alert-table-action-row'>
          <BlockIcon style={{ fontSize: 14 }} />
          {'3 '}
          {translate('monthsShort', language, true)}
        </div>
        <div className='alert-table-action-row'>
          {translate('thisSearch', language)}
        </div>
      </div>

      <div className='alert-table-action-column alert-table-exclude-column' onClick={(event) => excludePlayerInternal(event, true, 12)}>
        <div className='alert-table-action-row'>
          <BlockIcon style={{ fontSize: 14 }} />
          {'1 '}
          {translate('year', language, true)}
        </div>
        <div className='alert-table-action-row'>
          {translate('allSearches', language)}
        </div>
      </div>

    </div>
  );
};


interface AlertTableExcludedCellProps {
  row: PlayerOverview;
  language?: string;
  isShowAllExcludedPlayersToggled?: boolean;
  undoExcludePlayer?: (playerOverview: PlayerOverview) => void;
}

export const AlertTableExcludedCell: React.FC<AlertTableExcludedCellProps> = ({
  row,
  language,
  isShowAllExcludedPlayersToggled,
  undoExcludePlayer,
}) => {

  const undoExcludePlayerInternal = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (undoExcludePlayer) {
      undoExcludePlayer(row);
    }
  };

  return (
    <div className='alert-table-action-cell'>

      <div className='alert-table-action-column'>
        <div className='alert-table-action-row'>
          <BlockIcon style={{ fontSize: 14 }} />
        </div>
        <div className='alert-table-action-row'>
          {translate(isShowAllExcludedPlayersToggled ? 'allSearches' : 'thisSearch', language)}
        </div>
      </div>

      <div className='alert-table-action-column alert-table-excluded-column' onClick={(event) => undoExcludePlayerInternal(event)}>
        <div className='alert-table-action-row'>
          <RestoreIcon style={{ fontSize: 14 }} />
        </div>
        <div className='alert-table-action-row'>
          {translate('undo', language)}?
        </div>
      </div>

    </div>
  );
};


export const PlayerSeasonTableMinutesCell: React.FC<{ row: PositionGroupClubIteration }> = ({ row }) => {

  // total minutes info
  const minutesPlayed = row.minutes_played ?? 0;
  const percentagePlayed = Math.round((row.minutes_played_percentage ?? 0) * 100) + '%';
  const minutesColorIndex = Math.min(minutesPlayed / 150, 10);
  const minutesColor = minutesColorIndex ? getIndexColor(minutesColorIndex, 47) : 'transparent';

  return (
    <div className='player-season-table-minutes-cell'>
      <ProgressCircle
        size={18}
        progress={minutesColorIndex * 10}
        color={minutesColor}
        strokeWidth={12}
        trailColor='#00000022'
      />

      <div className='player-season-table-minutes-row'>
        {minutesPlayed} min

        <div style={{ fontSize: 11, color: '#000000bb' }}>
          {'(' + percentagePlayed + ')'}
        </div>
      </div>
    </div>
  );
};


interface IterationMetricCellProps {
  positionGroupClubIteration?: PositionGroupClubIteration,
  playerClubIteration?: PlayerClubIteration,
  property: string,
  metricGroupKey: string,
  metricGroupIsToggled: boolean,
  isColorBlind: boolean,
  roleConfig?: RoleConfig,
  positionGroup?: string,
}

export const IterationMetricCell: React.FC<IterationMetricCellProps> = ({
  positionGroupClubIteration,
  playerClubIteration,
  property,
  metricGroupKey,
  metricGroupIsToggled,
  isColorBlind,
  roleConfig,
  positionGroup,
}) => {

  if (!positionGroupClubIteration && !playerClubIteration) return null;

  // if playerClubIteration is provided and roleConfig is under construction, show build icon
  if (playerClubIteration) {
    const isBeingComputed = roleConfigIsBeingComputed(roleConfig);

    if (isBeingComputed) {
      return (
        <div className='table-cell'>
          <BuildIcon style={{ fontSize: 14, color: '#ffffffcc' }} />
        </div>
      );
    }
  }


  // if positionGroupClubIteration is provided, all metrics can be accessed directly, else we need to access the correct position_group_stats
  let metricMapAccessor: PositionGroupClubIteration | PlayerClubIterationPositionGroupData | undefined = positionGroupClubIteration;

  if (playerClubIteration && positionGroup) {
    const positionGroupForIteration = positionGroup === 'OVERALL' && !playerClubIteration.position_group_stats?.OVERALL
      ? playerClubIteration.position_groups[0]
      : positionGroup;

    metricMapAccessor = playerClubIteration.position_group_stats?.[positionGroupForIteration];
  }

  const metricMap: StringToAnyMap | undefined = (property === 'skill_rating' || property === 'physical_rating')
    ? metricMapAccessor
    : roleConfig
      ? metricMapAccessor?.custom_ratings
      : metricMapAccessor?.metrics;

  const metricKey = getNormalizedMetricName(property);

  const normalizedValue = metricMap?.[metricKey];

  // the metric is either available (value or '-'), blocked (blocked icon), or locked (locked icon)
  let metricStatus: 'available' | 'blocked' | 'locked' = 'available';

  if (!metricMapAccessor || !metricMapAccessor.minutes_played || normalizedValue === 'NOT_EXISTING') {
    metricStatus = 'blocked';
  }

  else {
    const metricIsPhysical = property === 'physical_rating' || isSkillcornerMetric(property) || (roleConfig && isPhysicalRoleConfig(roleConfig));

    const eventDataAvailable = positionGroupClubIteration !== undefined || playerClubIteration?.event_data_available;
    const eventDataExists = positionGroupClubIteration !== undefined || playerClubIteration?.event_data_exist;

    const physicalDataAvailable = positionGroupClubIteration?.physical_data_available || playerClubIteration?.physical_data_available;
    const physicalDataExists = positionGroupClubIteration?.physical_data_exist || playerClubIteration?.physical_data_exist;

    if ((metricIsPhysical && !physicalDataAvailable && physicalDataExists) || (!metricIsPhysical && !eventDataAvailable && eventDataExists)) {
      metricStatus = 'locked';
    }
  }

  if (metricStatus === 'blocked') {
    return (
      <div className={'table-cell table-cell-blocked' + (positionGroupClubIteration ? ' table-cell-blocked-dark' : ' table-cell-blocked-light')}/>
    );
  }

  else if (metricStatus === 'locked') {
    return (
      <div className={'table-cell table-cell-locked' + (positionGroupClubIteration ? ' table-cell-locked-dark' : ' table-cell-locked-light')}/>
    );
  }

  let displayValue = (normalizedValue !== undefined && normalizedValue !== null) ? Math.round(normalizedValue * 10) / 10 : '-';

  if (metricMap && metricGroupIsToggled && displayValue !== '-') {
    let actualValue = metricMap[property];

    const unit = playerMetricMap[property]?.unit;

    const isPercentage = unit === '%';
    if (actualValue && isPercentage) {
      actualValue = actualValue * 100;
    }

    const scalingFactor = unit === 'km'
      ? 0.001
      : 1;

    const roundingFactor =
      (metricGroupKey === 'attacking' && property !== 'n_shots_per_90') ||
      property === 'gk_postshot_xg_prevented_difference_per_shot' ||
      unit === 's'
        ? 100
        : unit === 'm'
          ? 1
          : 10;

    displayValue = actualValue !== undefined ? Math.round(actualValue * scalingFactor * roundingFactor) / (roundingFactor) : '-';

    if (displayValue !== '-' && unit) {
      const padding = unit === '%' ? '' : ' ';
      displayValue = displayValue + padding + unit;

      if (property === 'gk_penalty_saves_percentage') {
        displayValue = metricMap['gk_n_penalties_saved'] + '/' + metricMap['gk_n_penalties_faced'] + ' (' + displayValue + ')';
      }
    }
  }

  const backgroundColor = positionGroupClubIteration
    ? (isColorBlind ? getColorBlindRatingColor(normalizedValue ?? 0, 0.3) : getIndexColor(normalizedValue, 74))
    : (isColorBlind ? getColorBlindRatingColor(normalizedValue ?? 0, 0.7) : getRatingColor(normalizedValue));


  return (
    <div className='table-cell'>
      <div
        className='table-color-cell'
        style={{
          backgroundColor: backgroundColor,
          fontSize: isColorBlind ? 13 : 12,
          fontWeight: isColorBlind ? 700 : 500
        }}
      >
        {displayValue}
      </div>
    </div>
  );
};


interface PlayerTableAgeCellProps {
  row: StringToAnyMap;
  language?: string;
}


export const PlayerTableAgeCell: React.FC<PlayerTableAgeCellProps> = ({ row, language }) => {

  const birthDate = row.birth_date;
  const age = getAge(birthDate);

  if (!age) return (<div className='table-cell'>-</div>);

  return (
    <div className='table-cell flex-column'>

      <div>
        {age + ' ' + translate('years', language, true)}
      </div>

      <div style={{ fontSize: 12, color: '#00000099', marginTop: 2 }}>
        {birthDate ? dateStringToTextFormat(birthDate) : ''}
      </div>

    </div>
  );
};


export const OwnTeamTableAgeCell: React.FC<PlayerTableAgeCellProps> = ({ row, language }) => {

  if (row.name === 'tableSummary') {
    return (
      <div className='table-cell flex-column'>
        <div className='own-team-table-summary-cell-label'>
          {translate('average', language)}
        </div>
        {row.averageAge ? (row.averageAge + ' ' + translate('years', language, true)) : '-'}
      </div>
    );
  }

  return (
    <PlayerTableAgeCell row={row} language={language} />
  );
};


interface PlayerTableContractCellProps {
  row: StringToAnyMap;
  language?: string;
}


export const PlayerTableContractCell: React.FC<PlayerTableContractCellProps> = ({ row, language }) => {

  const contractExpiration = row.contract_expiration;
  const monthsLeft = getMonthsLeftOfContract(row.contract_expiration);

  if (monthsLeft === undefined) return (<div className='table-cell'>-</div>);

  return (
    <div className='table-cell flex-column'>

      <div>
        {monthsLeft + ' ' + translate('monthsShort', language)}
      </div>

      <div style={{ fontSize: 12, color: '#00000099', marginTop: 2 }}>
        {contractExpiration ? dateStringToTextFormat(contractExpiration) : ''}
      </div>

    </div>
  );
};


export const OwnTeamTableContractCell: React.FC<PlayerTableContractCellProps> = ({ row, language }) => {

  if (row.name === 'tableSummary') {
    return (
      <div className='table-cell flex-column'>
        <div className='own-team-table-summary-cell-label'>
          {translate('average', language)}
        </div>
        {row.averageMonthsLeftOfContract ? (row.averageMonthsLeftOfContract + ' ' + translate('monthsShort', language)) : '-'}
      </div>
    );
  }

  return (
    <PlayerTableContractCell row={row} language={language} />
  );
};


interface OwnTeamTableCountryCellProps {
  row: StringToAnyMap;
  language?: string;
}

export const OwnTeamTableCountryCell: React.FC<OwnTeamTableCountryCellProps> = ({ row, language }) => {

  if (row.name === 'tableSummary') {

    const nationalPercentage = row.numberOfPlayers ? Math.round((row.numberOfNatives / row.numberOfPlayers) * 100) : 0;

    return (
      <div className='table-cell flex-column' style={{ height: 46, marginTop: 4 }}>

        <div
          className='table-cell'
          style={{ fontSize: 12, color: '#000000cc', marginLeft: 4 }}
        >
          <PublicIcon style={{ fontSize: 16, color: '#353a48bb' }} />
          <div style={{ width: 26, marginLeft: 6 }}>
            {row.numberOfForeigners}
          </div>
        </div>

        <div className='table-cell' style={{ fontSize: 12, color: '#000000cc', marginLeft: 4 }}>
          <CountryFlag
            countryCode={row.nationalCountryCode}
            width={15}
            isWhiteBackground={true}
          />
          <div style={{ width: 26, marginLeft: 6 }}>
            {nationalPercentage + '%'}
          </div>
        </div>

      </div >
    );
  }

  const countryCode = row.country_code;
  const countryInfo = countryCodeToCountryInfo[countryCode];

  return (
    <div
      className='table-cell'
      title={countryInfo ? countryInfo.name[language ?? 'en'] : ''}
    >
      <CountryFlag
        countryCode={countryCode}
        width={16}
        showHyphenIfEmpty={true}
        isWhiteBackground={true}
      />
    </div>
  );
};


export const AcademyTableTeamCell: React.FC<{ row: StringToAnyMap }> = ({ row }) => {

  const playerTeamData = useRecoilValue(playerTeamDataSelector) ?? {};
  const teams = useRecoilValue(teamsState) ?? {};

  if (row.name === 'tableSummary') return '-';

  const teamIds = playerTeamData[row.id]
    ? Object.keys(playerTeamData[row.id]).filter(key => teams[key] && teams[key].isAcademyTeam)
    : [];

  const numberOfAdditionalTeams = teamIds.length - 2;
  const teamsToShow = teamIds.slice(0, 2);

  return (
    <div className='table-cell flex-column align-center' style={{ gap: 2}}>

      {teamsToShow.map((teamId, index) => {
        const team = teams[teamId];
        if (!team) return null;

        return (
          <div key={index}>
            {team.name}
          </div>
        );
      })}

      {numberOfAdditionalTeams > 0 && (
        <div style={{ fontSize: 11, color: '#00000099', marginLeft: -1 }}>
          {'+' + numberOfAdditionalTeams}
        </div>
      )}
    </div>
  );
};


interface AcademyTablePositionCellProps {
  row: StringToAnyMap;
  language?: string;
}

export const AcademyTablePositionCell: React.FC<AcademyTablePositionCellProps> = ({ row, language }) => {

  const playerTeamData = useRecoilValue(playerTeamDataSelector) ?? {};
  const teams = useRecoilValue(teamsState) ?? {};

  if (row.name === 'tableSummary') return '-';

  const positions = new Set<string>();
  Object.entries(playerTeamData[row.id] ?? {}).forEach(([teamId, position]) => {
    if (teams[teamId] && teams[teamId].isAcademyTeam) {
      positions.add(position);
    }
  });

  const numberOfAdditionalPositions = positions.size - 2;
  const positionsToShow = Array.from(positions).slice(0, 2);

  return (
    <div className='table-cell flex-column align-center' style={{ gap: 2}}>

      {positionsToShow.map((position, index) => {
        return (
          <div key={index}>
            {translate(position, language)}
          </div>
        );
      })}

      {numberOfAdditionalPositions > 0 && (
        <div style={{ fontSize: 11, color: '#00000099', marginLeft: -1 }}>
          {'+' + numberOfAdditionalPositions}
        </div>
      )}
    </div>
  );
};


interface OwnTeamTableEconomicCellProps {
  row: StringToAnyMap;
  property: string;
  language?: string;
}

export const OwnTeamTableEconomicCell: React.FC<OwnTeamTableEconomicCellProps> = ({
  row,
  property,
  language,
}) => {

  if (row.name === 'tableSummary') {
    return (
      <div className='table-cell flex-column'>
        <div className='own-team-table-summary-cell-label'>
          {translate('sum', language)}
        </div>
        {row[property + 'Total'] ? (getDisplayEconomicValue(row[property + 'Total'], true)) : '-'}
      </div>
    );
  }

  let value = row[property];

  if (value !== undefined && value !== null) {
    value = getDisplayEconomicValue(value, true);
  }

  return (
    <div className='table-cell'>
      {value}
    </div>
  );
};


interface OwnTeamTableLocalityCellProps {
  row: StringToAnyMap;
  language?: string;
}

export const OwnTeamTableLocalityCell: React.FC<OwnTeamTableLocalityCellProps> = ({ row, language }) => {

  if (row.name === 'tableSummary') {

    const localIcon = getLocalityIcon('local', 16, '#353a48bb');
    const nationalIcon = getLocalityIcon('national', 16, '#353a48bb');
    const internationalIcon = getLocalityIcon('international', 16, '#353a48bb');

    return (
      <div className='table-cell flex-column' style={{ height: 46, marginTop: 4 }}>
        <div
          className='table-cell'
          style={{ fontSize: 13, color: '#000000cc' }}
        >
          <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
            {localIcon}
          </div>
          <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
            {nationalIcon}
          </div>
          <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
            {internationalIcon}
          </div>
        </div>

        <div
          className='table-cell'
          style={{ fontSize: 13, color: '#000000cc' }}
        >
          <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
            {row.numberOfLocalPlayers}
          </div>
          <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
            {row.numberOfNationalPlayers}
          </div>
          <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
            {row.numberOfInternationalPlayers}
          </div>
        </div>
      </div>
    );
  }

  const icon = getLocalityIcon(row.locality, 18, 'dark');

  return (
    <div
      className='table-cell'
      title={row.locality ? translate(row.locality + 'Locality', language) : undefined}
    >
      {icon}
    </div>
  );
};


interface OwnTeamTableRoleCellProps {
  row: StringToAnyMap;
  isAcademyTable: boolean;
  tenant: string;
  language?: string;
}

export const OwnTeamTableRoleCell: React.FC<OwnTeamTableRoleCellProps> = ({
  row,
  tenant,
  isAcademyTable,
  language,
}) => {

  if (row.name === 'tableSummary') {

    if (isAcademyTable) {
      const trialistIcon = getRoleIcon('trialist', 16, '#353a48bb');
      const highPotentialPlayerIcon = getRoleIcon('highPotentialPlayer', 16, '#353a48bb');
      const performancePlayerIcon = getRoleIcon('performancePlayer', 16, '#353a48bb');
      const outplacementPlayerIcon = getRoleIcon('outplacementPlayer', 16, '#353a48bb');
      const transferTargetPlayerIcon = getRoleIcon('transferTarget', 16, '#353a48bb');

      return (
        <div className='table-cell flex-column' style={{ height: 46, marginTop: 4 }}>
          <div
            className='table-cell'
            style={{ fontSize: 13, color: '#000000cc' }}
          >
            <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
              {trialistIcon}
            </div>
            <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
              {highPotentialPlayerIcon}
            </div>
            <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
              {performancePlayerIcon}
            </div>
            <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
              {outplacementPlayerIcon}
            </div>
            <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
              {transferTargetPlayerIcon}
            </div>
          </div>

          <div
            className='table-cell'
            style={{ fontSize: 13, color: '#000000cc' }}
          >
            <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
              {row.roleMap.trialist ?? 0}
            </div>
            <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
              {row.roleMap.highPotentialPlayer ?? 0}
            </div>
            <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
              {row.roleMap.performancePlayer ?? 0}
            </div>
            <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
              {row.roleMap.outplacementPlayer ?? 0}
            </div>
            <div style={{ width: 27, display: 'flex', justifyContent: 'center' }}>
              {row.roleMap.transferTarget ?? 0}
            </div>
          </div>
        </div>
      );
    }

    const loanedInIcon = getRoleIcon('loanedIn', 16, '#353a48bb');
    const loanedOutIcon = getRoleIcon('loanedOut', 16, '#353a48bb');

    return (
      <div className='table-cell flex-column' style={{ height: 46, marginTop: 4 }}>
        <div
          className='table-cell'
          style={{ fontSize: 12, color: '#000000cc', paddingLeft: 4 }}
        >
          {loanedInIcon}
          <div style={{ marginLeft: 4 }}>
            {row.roleMap.loanedIn ?? 0}
          </div>
        </div>
        <div
          className='table-cell'
          style={{ fontSize: 12, color: '#000000cc', paddingLeft: 4 }}
        >
          {loanedOutIcon}
          <div style={{ marginLeft: 4 }}>
            {row.roleMap.loanedOut ?? 0}
          </div>
        </div>
      </div>
    );
  }

  const role = isAcademyTable ? row.academyRole : row.role;
  const icon = getRoleIcon(role, 18, 'dark', getTenantLightColor(tenant));

  return (
    <div
      className='table-cell'
      title={role ? translate(role, language, undefined, undefined, tenant) : undefined}
    >
      {icon}
    </div>
  );
};


interface SeasonTableSeasonCellProps {
  row: PlayerClubIteration;
  positionGroup: string;
  language?: string;
}

export const SeasonTableSeasonCell: React.FC<SeasonTableSeasonCellProps> = ({
  row,
  positionGroup,
  language,
}) => {

  // total minutes info
  const selectedPositionGroupKeyForIteration = positionGroup === 'OVERALL' && !row.position_group_stats?.OVERALL
    ? row.position_groups[0]
    : positionGroup;

  const positionGroupStats = row.position_group_stats?.[selectedPositionGroupKeyForIteration];
  const totalMinutesPlayed = positionGroupStats?.minutes_played ?? 0;
  const minutesColorIndex = Math.min(totalMinutesPlayed / 150, 10);
  const minutesColor = minutesColorIndex ? getIndexColor(minutesColorIndex, 60) : 'transparent';

  // if selectedPositionGroupKey is OVERALL, we want to display positions similar to other tables, and then minutes played
  // if selectedPositionGroupKey is not OVERALL, we want to display minutes played for each position
  let positionColumn: JSX.Element | undefined = undefined;
  if (positionGroup === 'OVERALL') {
    const primaryPositions = row.position_info?.primary_positions ?? [];
    const secondaryPositions = row.position_info?.secondary_positions ?? [];

    const primaryPositionsToUse = primaryPositions.slice(0, 3);
    const primaryPositionString = getDisplayPositions(primaryPositionsToUse, language);

    let secondaryPositionsToUse = [];
    let secondaryPositionString: string | undefined = undefined;

    if (primaryPositionsToUse.length <= 1 && secondaryPositions.length > 0) {
      secondaryPositionsToUse = secondaryPositions.slice(0, 2);
      secondaryPositionString = getDisplayPositions(secondaryPositionsToUse, language);
    }

    const primaryFontSize = primaryPositionsToUse.length > 2 ? 10 : secondaryPositionsToUse.length > 1 ? 11 : 12;
    const secondaryFontSize = 12 - secondaryPositionsToUse.length;

    positionColumn = (
      <div className='season-table-season-cell-position-column' style={{ fontSize: primaryFontSize, gap: 4 }}>
        <div className='season-table-season-cell-position-row' style={{ justifyContent: 'left', gap: 4 }}>
          {primaryPositionString}

          {secondaryPositionString && (
            <div style={{ fontSize: secondaryFontSize, color: '#ffffffbb' }}>
              {'(' + secondaryPositionString + ')'}
            </div>
          )}
        </div>

        <div style={{ fontSize: 11 }}>
          {totalMinutesPlayed} min
        </div>
      </div>
    );
  }

  else {
    let positions: StringToAnyMap[] = positionGroupStats?.position_info?.positions ?? [];
    let positionColor: string | undefined = undefined;
    let minutesColor: string | undefined = undefined;

    if (positions.length === 0) {
      positions = [{ position: '-', percentage: 0, minutes: 0 }];
      positionColor = '#ffffff99';
      minutesColor = '#ffffff66';
    }

    const fontSize = positions.length > 2 ? 10 : 11;

    positionColumn = (
      <div className='season-table-season-cell-position-column' style={{ fontSize: fontSize }}>
        {positions.map((position, index) => {
          return (
            <div key={index} className='season-table-season-cell-position-row' style={{ color: positionColor }}>
              {getDisplayPositions([position.position], language)}

              <div style={{ color: minutesColor }}>
                {position.minutes} min
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className='season-table-season-cell'>

      <div className='season-table-season-cell-column'>

        <div className='season-table-season-cell-row'>
          <CompetitionFlag
            competitionId={row.competition_id}
            width={14}
            seasonToShow={row.season}
            containerStyle={{ gap: 3, marginLeft: 1 }}
            imageStyle={{ marginRight: 2 }}
            smallFontSize={[11]}
            smallFontSizeThreshold={[20]}
          />
        </div>

        <div className='season-table-season-cell-row'>
          <ClubLogo
            club={row.club}
            size={14}
            showClubName={true}
            containerStyle={{ gap: 5, marginLeft: 1 }}
            smallFontSize={11}
            smallFontSizeThreshold={20}
          />
        </div>

      </div>

      {positionColumn}

      <div className='season-table-season-cell-minutes-column'>
        <ProgressCircle
          size={14}
          progress={minutesColorIndex * 10}
          color={minutesColor}
          strokeWidth={9}
          trailColor='#ffffff44'
        />
      </div>

    </div>
  );
};


export const CareerTableNationalCell: React.FC<{ row: StringToAnyMap }> = ({ row }) => {

  return (
    <div className='table-cell' style={{ justifyContent: 'left', paddingLeft: 8 }}>
      <ClubLogo
        club={row.club}
        size={14}
        showClubName={true}
        containerStyle={{ gap: 6, marginLeft: 1 }}
      />
    </div>
  );
};


export const CareerTableClubCell: React.FC<CareerTableColumnCellProps> = ({
  row,
  isExpanded,
  onMouseEnter,
  onMouseLeave,
  onClick,
}) => {

  const period = row.firstSeason + (row.isCurrentPeriod ? ' -' : row.lastSeason ? (' - ' + row.lastSeason) : '');

  return (
    <div className='flex-column' style={{ width: '100%', height: '100%' }}>

      <div
        className='season-table-season-cell'
        style={{ minHeight: 50, maxHeight: 50 }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      >
        <ClubLogo
          club={row.club}
          size={24}
          containerStyle={{ marginLeft: 0, marginRight: 8 }}
        />

        <div className='season-table-season-cell-column'>
          <div style={{ fontSize: 13 }}>
            {row.club.name}
          </div>
          <div style={{ marginTop: 4, color: '#ffffffdd' }}>
            {period}
          </div>
        </div>

        <div className={'career-table-accordion-icon-container' + (isExpanded ? ' accordion-icon-container-rotated' : '')}>
          {<KeyboardArrowDownIcon style={{ fontSize: 18 }} />}
        </div>
      </div>

      <div className='career-table-season-cell-row-even' style={{ height: '100%' }}>
        {isExpanded && row.iterations.map((iteration: PlayerClubIteration, index: number) => {
          return (
            <div
              key={index}
              className={
                'fade-in career-table-season-cell-row'
                + (index === row.iterations.length - 1 ? ' career-table-season-cell-last-row' : '')
                + (index === 0 ? ' career-table-season-cell-first-row' : '')
                + (index % 2 === 0 ? ' career-table-season-cell-row-even' : ' career-table-season-cell-row-odd')
              }
            >

              <CompetitionFlag
                competitionId={iteration.competition_id}
                width={14}
                containerStyle={{ justifyContent: 'left', gap: 9, marginLeft: 10, marginRight: 4 }}
              />

              <div style={{ color: '#ffffffdd' }}>
                {iteration.season}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


export const CareerTableValueCell: React.FC<CareerTableCellProps> = ({
  row,
  isNationalTeam,
  property,
  isExpanded,
  onMouseEnter,
  onMouseLeave,
  onClick,
  language
}) => {

  return (
    <div className='flex-column' style={{ width: '100%', height: '100%' }}>

      <div
        className='flex-row align-center justify-center'
        style={{
          minHeight: isNationalTeam ? 37 : 50,
          maxHeight: isNationalTeam ? 37 : 50,
          width: '100%'
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
      >
        {property === 'position' && (
          <PositionCell
            row={row}
            language={language}
            isDarkBackground={true}
            horizontal={true}
          />
        )}

        {property !== 'position' && row.basic_stats[property]}
      </div>

      <div className='career-table-season-cell-row-even' style={{ height: '100%'}}>
        {isExpanded && row.iterations.map((iteration: PlayerClubIteration, index: number) => {
          return (
            <div
              key={index}
              className={
                'fade-in justify-center career-table-season-cell-row'
                + (index === row.iterations.length - 1 ? ' career-table-season-cell-last-row' : '')
                + (index === 0 ? ' career-table-season-cell-first-row' : '')
                + (index % 2 === 0 ? ' career-table-season-cell-row-even' : ' career-table-season-cell-row-odd')
              }
            >
              {property === 'position' && (
                <PositionCell
                  row={iteration}
                  language={language}
                  isDarkBackground={true}
                  horizontal={true}
                />
              )}

              {property !== 'position' && iteration.basic_stats[property]}
            </div>
          );
        })}
      </div>
    </div>
  );
};


export const TeamTableTeamCell: React.FC<{ row: StringToAnyMap, index: number }> = ({ row, index }) => {

  let clubName = row.club_name;
  const clubNameArray = clubName.split(' ');

  if (clubNameArray.length > 2 && clubName.length > 25) {
    clubName = clubNameArray[0] + ' ' + clubNameArray[clubNameArray.length - 1];
  }

  return (
    <div className='table-cell' style={{ paddingLeft: 12, paddingRight: 4, justifyContent: 'left' }}>
      <div className='team-table-team-cell-rank'>
        {index + 1}
      </div>

      <ClubLogo
        clubLogoUrl={row.club_logo_url}
        clubName={clubName}
        size={18}
        showClubName={true}
        containerStyle={{ justifyContent: 'left', gap: 6, fontSize: 12, marginLeft: 1 }}
        smallFontSize={11}
        smallFontSizeThreshold={12}
        maxInfoWidth={97}
      />
    </div>
  );
};


interface PlayerSimpleTableTeamIconCellProps {
  row: StringToAnyMap;
  disablePlayersWithoutEventData?: boolean;
  isGoalkeeper?: boolean;
}

export const PlayerSimpleTableTeamIconCell: React.FC<PlayerSimpleTableTeamIconCellProps> = ({
  row,
  disablePlayersWithoutEventData,
  isGoalkeeper
}) => {

  const playerTeamData = useRecoilValue(playerTeamDataSelector) ?? {};
  const teams = useRecoilValue(teamsState) ?? {};

  if (disablePlayersWithoutEventData && !row.event_data_available) {
    if (row.event_data_exist) {
      return (
        <div className='table-cell'>
          <LockIcon style={{ fontSize: 18, color: '#353a48' }} />
        </div>
      );
    }
    return (
      <div className='table-cell'>
        <BlockIcon style={{ fontSize: 18, color: '#353a48' }} />
      </div>
    );
  }

  if (isGoalkeeper !== undefined && (isGoalkeeper !== row.position_info?.primary_positions?.includes('GK'))) {
    return (
      <div className='table-cell'>
        <BlockIcon style={{ fontSize: 18, color: '#353a48' }} />
      </div>
    );
  }

  const currentTeamIds = playerTeamData[row.id] ? Object.keys(playerTeamData[row.id]) : [];
  const teamEntries = currentTeamIds.map(teamId => ({ teamId, isAcademyTeam: teams[teamId]?.isAcademyTeam ?? false }));
  const currentTeamIcon = getCurrentTeamIcon(teamEntries, 18, 'dark');

  if (currentTeamIcon) {
    return (
      <div className='table-cell'>
        {currentTeamIcon}
      </div>
    );
  }

  return <div>&nbsp;</div>;
};


interface AccessTableToggleCellProps {
  isToggled: boolean;
  setIsToggled?: (isToggled: boolean, callBackProperty?: string, event?: React.MouseEvent) => void;
}

export const AccessTableToggleCell: React.FC<AccessTableToggleCellProps> = ({
  isToggled,
  setIsToggled,
}) => {

  if (setIsToggled) {
    return (
      <div className='table-cell'>
        <div
          className='flex-row align-center justify-center access-table-option access-table-option-clickable'
          onClick={() => setIsToggled(!isToggled)}
        >
          <Toggle
            isToggled={isToggled}
            setIsToggled={() => {}}
            boxShadow={'0px 0px 2px 1px #00000020'}
            toggledBackgroundColor={'#30a93a'}
            untoggledBackgroundColor={'#c33838'}
            circleColor={'#ffffff'}
            isSmall={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className='table-cell'>
      {
        isToggled
          ? <CheckIcon style={{ fontSize: 18, color: '#2bff3d' }} />
          : <CloseIcon style={{ fontSize: 18, color: '#ff4848' }} />
      }
    </div>
  );
};


interface AccessTableOptionCellProps {
  row: AccessConfig;
  property: 'logisticTeamAccess' | 'reportAccess';
  handleOptionClick?: (value: string) => void;
  language?: string;
}

export const AccessTableOptionCell: React.FC<AccessTableOptionCellProps> = ({ row, property, handleOptionClick, language }) => {


  const [accountTableOptionModalType, setAccountTableOptionModalType] = useState<'logisticTeamAccess' | 'reportAccess' | undefined>(undefined);


  return (
    <div className='table-cell'>

      <Dialog
        open={accountTableOptionModalType !== undefined}
        onClose={(event: React.MouseEvent) => {
          setAccountTableOptionModalType(undefined);
          event?.stopPropagation();
        }}
        slotProps={{ paper: modalPaperProps }}
      >
        <AccessTableOptionModal
          accountTableOptionModalType={accountTableOptionModalType}
          closeModal={(event?: React.MouseEvent) => {
            setAccountTableOptionModalType(undefined);
            event?.stopPropagation();
          }}
          selectedOption={row[property]}
          handleOptionClick={handleOptionClick}
        />
      </Dialog>

      <div
        className={'access-table-option' + (handleOptionClick ? ' access-table-option-clickable' : '')}
        title={translate('edit', language)}
        onClick={
          handleOptionClick
            ? (event) => {
              setAccountTableOptionModalType(property);
              event.stopPropagation();
            }
            : undefined
        }
      >
        {getAccessTableOptionIcon(row[property], 16, 'colored')}
      </div>
    </div>
  );
};
